import {
  ApiError,
  TutorLimitedResponse,
  TutorName,
  TutorStudentsService,
  TutorsService,
} from "client/openapi";
import AssignedPeople, {
  AssignedPeopleLoading,
} from "pages/manage/ProfilePageResources/AssignedPeople";
import { useEffect, useState } from "react";
import { LuGraduationCap } from "react-icons/lu";

/** The student's tutors, and the organization's to pick from. */
type Lists = [TutorLimitedResponse[], TutorName[]];

/** Both lists. Only names are needed of the organization's tutors. */
function loadLists(studentId: number, orgId: number): Promise<Lists> {
  return Promise.all([
    TutorStudentsService.getAllTutorsAssignedToAStudent({ studentId }),
    TutorsService.getTutorNamesByOrganization({ orgId }),
  ]);
}

/** How long lists started early are used: from a hover to its click. */
const EARLY_FOR_MS = 5000;

type Early = {
  studentId: number;
  orgId: number;
  at: number;
  lists: Promise<Lists>;
  /** Once they are in, so the section can open with them. */
  loaded?: Lists;
};

/** The lists prefetchTutors started, for the next section shown. */
let early: Early | undefined;

function earlyFor(studentId: number, orgId: number) {
  return early?.studentId === studentId &&
    early.orgId === orgId &&
    Date.now() - early.at < EARLY_FOR_MS
    ? early
    : undefined;
}

/**
 * Start loading a student's section before it shows (as the pointer reaches
 * what opens it), so it can open with its data.
 */
export function prefetchTutors(studentId: number, orgId: number) {
  if (earlyFor(studentId, orgId)) return;
  const entry: Early = {
    studentId,
    orgId,
    at: Date.now(),
    lists: loadLists(studentId, orgId),
  };
  // A failure is reported by the section that shows them.
  entry.lists.then(
    (loaded) => {
      entry.loaded = loaded;
    },
    () => {}
  );
  early = entry;
}

/**
 * The student's tutors, as the tutor page lists its students, with Assign
 * Tutor to pair one of the organization's tutors (archived ones left out).
 * Until both lists are loaded, a spinner shows in the box.
 */
export default function Tutors({
  studentId,
  orgId,
  onChange,
}: {
  studentId: number;
  orgId: number;
  /** A pairing was added or removed. */
  onChange?: () => void;
}) {
  const [lists, setLists] = useState(() => earlyFor(studentId, orgId)?.loaded);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let shown = true;
    const pending =
      earlyFor(studentId, orgId)?.lists ?? loadLists(studentId, orgId);
    early = undefined;
    pending
      .then((loaded) => {
        if (shown) setLists(loaded);
      })
      .catch((e: ApiError) => {
        console.error(`Unable to fetch the student's tutors: ${e.message}`);
        if (shown) setError("Unable to load tutors.");
      });
    return () => {
      // Another student's lists load afresh.
      shown = false;
      setLists(undefined);
      setError(undefined);
    };
  }, [studentId, orgId]);

  const section = {
    id: "student-tutors",
    title: "Tutors",
    icon: <LuGraduationCap strokeWidth={1.75} />,
  };

  if (!lists) return <AssignedPeopleLoading {...section} error={error} />;

  const [tutors, orgTutors] = lists;
  return (
    <AssignedPeople<TutorName>
      {...section}
      noun="Tutor"
      assigned={tutors}
      candidates={orgTutors}
      viewPath={(tutor) => `/manage/tutors/${tutor.id}`}
      assign={(tutor) =>
        TutorStudentsService.createTutorStudentRelationship({
          tutorId: tutor.id,
          studentId,
        })
      }
      remove={(tutor) =>
        TutorStudentsService.removeTutorStudentRelation({
          tutorId: tutor.id,
          studentId,
        })
      }
      onChange={onChange}
    />
  );
}
